import React from "react";
import {
  formatPercent,
  getAchievementToneClass,
} from "../utils/formatters";
import PaginationControls from "./PaginationControls";

export default function EmployeeList({
  loading,
  error,
  employees,
  selectedEmployeeId,
  onSelect,
  page,
  totalPages,
  onPageChange,
}) {
  // ── Sort state ────────────────────────────────────────────────────────────
  const [sortConfig, setSortConfig] = React.useState({
    key: null,
    direction: "asc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedEmployees = React.useMemo(() => {
    const list = [...(employees || [])];
    if (!sortConfig.key) return list;

    return list.sort((a, b) => {
      let aVal = parseFloat(a[sortConfig.key]);
      let bVal = parseFloat(b[sortConfig.key]);
      if (isNaN(aVal)) aVal = 0;
      if (isNaN(bVal)) bVal = 0;
      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [employees, sortConfig]);

  const renderSortIcons = (key) => (
    <span style={{ marginLeft: "6px", fontSize: "11px" }}>
      <span style={{ opacity: sortConfig.key === key && sortConfig.direction === "asc" ? 1 : 0.3 }}>▲</span>
      <span style={{ opacity: sortConfig.key === key && sortConfig.direction === "desc" ? 1 : 0.3, marginLeft: "2px" }}>▼</span>
    </span>
  );

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <section className="panel list-panel">
        <div className="section-loader">
          <span className="loader-spinner" aria-hidden="true" />
          <span className="loader-text">
            Loading employees<span className="loader-dots" aria-hidden="true" />
          </span>
        </div>
        <div className="list-header">
          <div className="skeleton-line w-40" />
          <div className="skeleton-pill w-20" />
        </div>
        <div className="table-shell">
          <table className="dashboard-table">
            <thead>
              <tr>{Array.from({ length: 6 }).map((_, i) => <th key={i}><div className="skeleton-line w-70" /></th>)}</tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }).map((_, row) => (
                <tr key={row}>
                  {Array.from({ length: 6 }).map((_, col) => (
                    <td key={col}><div className="skeleton-line w-90" /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="panel list-panel">
        <div className="section-error">{error}</div>
      </section>
    );
  }

  return (
    <section className="panel list-panel">
      <div className="list-header">
        <h3 className="section-heading">Employee Selection</h3>
        <span className="count-pill">{(employees || []).length} employees</span>
      </div>

      {!sortedEmployees.length ? (
        <div className="empty-state">No employees found for the selected filters.</div>
      ) : (
        <>
          <div className="table-shell">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Sales Officer</th>
                  <th>Designation</th>
                  <th onClick={() => handleSort("visits")} style={{ cursor: "pointer" }}>
                    Total Visits {renderSortIcons("visits")}
                  </th>
                  <th onClick={() => handleSort("coverage")} style={{ cursor: "pointer" }}>
                    Coverage % {renderSortIcons("coverage")}
                  </th>
                  <th>Missed Doctors</th>
                </tr>
              </thead>

              <tbody>
                {sortedEmployees.map((employee) => {
                  // Highlight whichever identifier the parent is tracking.
                  const isSelected =
                    selectedEmployeeId === employee.employee_code ||
                    selectedEmployeeId === employee.id;

                  return (
                    <tr
                      key={employee.id || employee.employee_code}
                      className={isSelected ? "row-selected" : ""}
                      // ✅ FIX: always pass the full employee object
                      onClick={() => onSelect(employee)}
                    >
                      <td className="linkish">{employee.employee_code}</td>

                      <td>
                        {/* ✅ FIX: no e.stopPropagation needed — both paths
                            now call onSelect(employee) consistently */}
                        <span
                          className="employee-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelect(employee);
                          }}
                        >
                          {employee.name}
                        </span>
                      </td>

                      <td>
                        <span className="designation-pill">{employee.designation}</span>
                      </td>

                      <td>{employee.visits}</td>

                      <td>
                        <span className={`coverage-pill ${getAchievementToneClass(employee.coverage)}`}>
                          {formatPercent(employee.coverage)}
                        </span>
                      </td>

                      <td className={employee.missedDoctors > 0 ? "tone-danger" : "tone-success"}>
                        {employee.missedDoctors}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <PaginationControls
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </>
      )}
    </section>
  );
}